// Local testing only: open the Sky with ?demo (or ?demo=400) on the dev
// server and it is filled with made-up thoughts, so the busy Sky can be felt
// before it is real. They live in this browser only: never sent to the
// server, and this does nothing in a production build. ?demo=0 turns it off.
import type { Thought } from '../types';
import { rng } from './skyLayout';

const LINES: [string, string, string][] = [
  ['lonely', '🌙', 'Some nights the quiet in my room is louder than any noise.'],
  ['lonely', '😶', 'I am surrounded by people all day and still feel unseen.'],
  ['lonely', '🌧️', 'Moved to a new city. I talk to my plants more than to people.'],
  ['lonely', '💭', 'I keep scrolling, hoping someone will text first.'],
  ['heartbreak', '💔', 'It has been four months and I still check if you are online.'],
  ['heartbreak', '🥀', 'We did not fight. We just slowly stopped choosing each other.'],
  ['heartbreak', '🌊', 'I deleted the photos but I still know them by heart.'],
  ['work', '😮‍💨', 'My boss keeps adding work and I feel tired every single day.'],
  ['work', '📚', 'Exams next week and my mind just will not focus.'],
  ['work', '🔥', 'Burnt out but everyone thinks I am doing great.'],
  ['work', '☕', 'Third coffee, still behind. I wish I could just rest without guilt.'],
  ['family', '🏠', 'Today I finally told my sister how tired I have been, and she listened.'],
  ['family', '🫂', 'My parents love me, I know. I just wish they understood me.'],
  ['family', '🕯️', 'Home feels heavy since dad got sick.'],
  ['selfworth', '🪞', 'I compare myself to everyone and always come second.'],
  ['selfworth', '🌱', 'Trying to believe I am enough, one small day at a time.'],
  ['selfworth', '😔', 'Every small mistake feels like proof that I am a failure.'],
  ['anxiety', '🌀', 'My heart races before every call. Nobody notices.'],
  ['anxiety', '😵‍💫', 'Overthinking a message I sent three hours ago.'],
  ['anxiety', '🌒', 'It is 3am and my thoughts will not stop running.'],
  ['grief', '🕊️', 'I miss my grandmother so much today. Her tea, her laugh.'],
  ['grief', '🌾', 'One year since you left. The world kept moving, I did not.'],
  ['anger', '😤', 'So tired of being the only one who always says sorry.'],
  ['anger', '⛈️', 'It is unfair and I am allowed to be angry about it.'],
  ['hope', '🌻', 'Small win: I went for a walk today and the sun felt nice.'],
  ['hope', '🌈', 'Six months sober. Still hard, but I am still here.'],
  ['hope', '✨', 'Someone was kind to me today for no reason. It helped.'],
  ['hope', '🌅', 'I think things are slowly getting a little better.'],
  ['other', '🍃', 'Just needed to put this somewhere. Thank you for reading.'],
  ['other', '☁️', 'Not sure what I feel. Maybe just a bit empty.'],
];
const REPLIES = ['You are not alone in this 🤍', 'Sending you a warm hug.', 'I felt this too. It gets lighter.', 'Proud of you for sharing.', 'Be gentle with yourself today.'];
const NAMES = ['Soft Petal', 'Quiet River', 'Golden Fern', 'Misty Moon', 'Warm Lantern'];

function demoCount(): number {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 0;
  try {
    const q = new URLSearchParams(window.location.search).get('demo');
    // Remember it for this tab: opening a thought changes the address.
    if (q !== null) sessionStorage.setItem('zuzu_sky_demo', q === '' ? '240' : q);
    return Math.max(0, Math.min(5000, Number(sessionStorage.getItem('zuzu_sky_demo')) || 0));
  } catch { return 0; }
}

let cache: Thought[] | null = null;
function demoThoughts(n: number): Thought[] {
  if (cache?.length === n) return cache;
  const rand = rng(42);
  cache = Array.from({ length: n }, (_, i) => {
    const [feeling, emoji, text] = LINES[Math.floor(rand() * LINES.length)];
    const nReplies = rand() < 0.22 ? 1 + Math.floor(rand() * 3) : 0;
    return {
      id: `demo-${String(i).padStart(5, '0')}`,
      text, emoji, x: 50, y: 50,
      size: 10 + rand() * 20,
      feelings: [feeling],
      meToo: Math.floor(rand() * 6),
      replies: Array.from({ length: nReplies }, (_, k) => ({
        id: `demo-${i}-r${k}`, text: REPLIES[(i + k) % REPLIES.length], name: NAMES[(i * 3 + k) % NAMES.length], submittedAt: '',
      })),
    };
  });
  return cache;
}

/** The Sky's thoughts, with the made-up ones first (older), when testing. */
export function withDemo(thoughts: Thought[]): Thought[] {
  const n = demoCount();
  return n ? [...demoThoughts(n), ...thoughts] : thoughts;
}
